import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  Invoice,
  InvoiceStateId,
  InvoiceStateType,
  Permission,
} from 'src/app/api/models';

import {
  ConfirmationDialogComponent,
  ConfirmationDialogDetail,
  ConfirmationDialogModel,
} from '../../components/confirmation-dialog/confirmation-dialog.component';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { UserService } from '../../service/user.service';
import {
  financingHoldsTooltip,
  financingOnHoldRefusal,
  financingOnHoldRefusalText,
} from '../../shared/financing-holds/financing-hold-text';

// The states out of which a financing hold refuses Accept: the advance that
// finances the invoice, and the one that leads to the final report
// (add-financing-holds, design D7).
const HOLD_GATED_ACCEPT_STATES: ReadonlyArray<InvoiceStateId> = [
  InvoiceStateId.WaitingForFinancing,
  InvoiceStateId.WaitingFinalPayment,
];

@Component({
  selector: 'app-invoice-state-widget',
  templateUrl: './invoice-state-widget.component.html',
  styleUrls: ['./invoice-state-widget.component.scss'],
})
export class InvoiceStateWidgetComponent implements OnInit {
  public invoice$: Observable<Invoice>;
  // AI: Gate the reset control on the same permission the endpoint checks
  // (internal/coolapi/invoice_workflow.go -> authz.PermInvoiceReset), not on
  // the name of a role that currently grants it, so the button and the
  // endpoint cannot drift when rolePermissions changes. Emits nothing until
  // the current user loads, so the template must treat null (unknown) as
  // distinct from false (denied) -- see the Reset control in the template.
  public canReset$: Observable<boolean>;

  // AI: Build the missing-permission copy from the generated enum rather than
  // writing 'invoice:reset' into the template, for the same reason canReset$
  // gates on Permission.InvoiceReset: the name the user is told to relay must
  // be the name the endpoint actually checks.
  public readonly resetPermissionTooltip = `Requires the ${Permission.InvoiceReset} permission`;

  // Accept and Reject are gated on invoice:advance, the permission the
  // endpoint checks for both (add-financing-holds, design D11), exactly as
  // Reset is gated on invoice:reset: null until the user loads, then true or
  // false.
  public canAdvance$: Observable<boolean>;
  public readonly advancePermissionTooltip = `Requires the ${Permission.InvoiceAdvance} permission`;

  public data$;
  public form: UntypedFormGroup;
  public showHistory = false;

  public locked = false;

  constructor(
    private invoicePeerService: InvoicePeerService,
    private userService: UserService,
    private builder: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    public dialog: MatDialog
  ) {
    this.form = this.builder.group({
      reason: [null],
    });
  }

  // The reason is kept when the event is refused, so the handler can retry
  // once the hold is released.
  public accept(invoice: Invoice): void {
    this.invoicePeerService
      .acceptState(invoice, this.form.value.reason)
      .pipe(catchError((err: unknown) => this.showFinancingHoldRefusal(err, 'Cannot accept the invoice')))
      .subscribe(() => this.form.reset());
  }

  public reject(invoice: Invoice): void {
    this.invoicePeerService
      .rejectState(invoice, this.form.value.reason)
      .subscribe(() => this.form.reset());
  }

  // A financing hold refuses Accept out of WaitingForFinancing and
  // WaitingFinalPayment. The server remains the authority: this only keeps
  // the button from promising what it would refuse.
  public holdBlocksAccept(invoice: Invoice): boolean {
    const state = invoice.state?.id;
    return !!invoice.financing_on_hold && state !== undefined && HOLD_GATED_ACCEPT_STATES.includes(state);
  }

  // What the Accept control explains while it is disabled: the missing
  // permission first, since nothing else matters without it, then the holds.
  public acceptTooltip(invoice: Invoice, canAdvance: boolean | null): string {
    if (canAdvance === false) {
      return this.advancePermissionTooltip;
    }
    if (this.holdBlocksAccept(invoice)) {
      return financingHoldsTooltip(invoice.financing_holds);
    }
    return '';
  }

  public reset(invoice: Invoice): void {
    const reason = (this.form.value.reason ?? '').trim();
    // AI: A reason is mandatory for every reset (the backend rejects an empty
    // reason with 422). Block the submit before opening the dialog.
    if (!reason) {
      this.snackBar.open(
        'A reason is required to reset the invoice state.',
        'Dismiss',
        { duration: 5000 }
      );
      return;
    }

    const isCompleted = invoice.state?.id === InvoiceStateId.Completed;
    const dialogData = new ConfirmationDialogModel(
      'Confirmation Required',
      isCompleted
        ? 'This invoice is Completed. Resetting rewinds it to PreCheck. The ' +
          'following have already happened and will NOT be undone automatically:'
        : 'Are you sure you want to reset invoice state?',
      undefined,
      undefined,
      isCompleted ? this.completedResetConsequences() : undefined
    );

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.invoicePeerService.resetState(invoice, reason).subscribe({
        next: () => {
          this.form.reset();
          this.snackBar.open('Invoice state reset.', 'Dismiss', {
            duration: 3000,
          });
        },
        error: (err: HttpErrorResponse) => {
          this.snackBar.open(this.resetErrorMessage(err), 'Dismiss', {
            duration: 6000,
          });
        },
      });
    });
  }

  public canAccept(invoice: Invoice): boolean {
    const current = invoice.state;
    if (current) {
      if (current.type === InvoiceStateType.Waiting) {
        return true;
      }
    }
    return false;
  }

  // AI: Mirror canAccept/canReject with a state predicate so the reset control
  // is not actionable in a state the backend would reject with 400. The backend
  // FSM (apps/cctool-go/internal/invoicestate/fsm.go) is the source of truth:
  // every InvoiceStateId is a valid reset source except PreCheck, which is
  // reset's own destination.
  public canReset(invoice: Invoice): boolean {
    return invoice.state?.id !== InvoiceStateId.PreCheck;
  }

  public canReject(invoice: Invoice): boolean {
    const current = invoice.state;
    if (current) {
      if (current.type === InvoiceStateType.Waiting) {
        return true;
        // TODO add api to list possible actions
        /*
        if (current.failureStateId) {
          return true;
        }
        */
      }
    }
    return false;
  }

  public toggleHistory(): void {
    this.showHistory = !this.showHistory;
  }

  public ngOnInit() {
    this.invoice$ = this.invoicePeerService.getInvoice();
    this.canReset$ = this.userService.hasPermission(Permission.InvoiceReset);
    this.canAdvance$ = this.userService.hasPermission(Permission.InvoiceAdvance);

    this.invoicePeerService.getState().subscribe((state) => {
      if (state === 'locked') {
        this.locked = true;
      }
      if (state === 'unlocked') {
        this.locked = false;
      }
    });
  }

  private completedResetConsequences(): ConfirmationDialogDetail[] {
    return [
      {
        label: 'Financing payment',
        value: 'Already issued — will NOT be automatically reversed.',
      },
      {
        label: 'End-customer invoice',
        value: 'Already sent — will NOT be automatically recalled.',
      },
      {
        label: 'Settlement & report',
        value: 'Already generated — will NOT be automatically undone.',
      },
    ];
  }

  // A 422 financing_on_hold is shown with the holds it lists; any other
  // error propagates as before.
  private showFinancingHoldRefusal(err: unknown, lead: string): Observable<never> {
    const holds = financingOnHoldRefusal(err);
    if (!holds) {
      return throwError(err);
    }
    this.snackBar.open(financingOnHoldRefusalText(lead, holds), 'Dismiss', {
      duration: 10000,
      panelClass: 'multiline-snackbar',
    });
    return EMPTY;
  }

  private resetErrorMessage(err: HttpErrorResponse): string {
    if (err.status === 400) {
      return 'This invoice cannot be reset from its current state.';
    }
    if (err.status === 403) {
      return 'You do not have permission to reset invoices.';
    }
    if (err.status === 422) {
      return 'A reason is required to reset the invoice state.';
    }
    return 'Failed to reset invoice state. Please try again.';
  }
}
